// src/components/ForgotPassword.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

function ForgotPassword() {
  const [email, setEmail]     = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);          // always show success — avoids email enumeration
    } catch (err) {
      // Still show success message for security; log internally
      console.error("Forgot-password error:", err);
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');
        :root { --gold:#C9A84C; --gold-light:#E8C97A; --dark:#0A0A0A; }

        .auth-root {
          font-family:'Jost',sans-serif; background:var(--dark); min-height:100vh;
          display:flex; flex-direction:column; align-items:center; justify-content:center;
          position:relative; overflow:hidden; padding:6rem 1.5rem 3rem;
        }
        .auth-root::before {
          content:''; position:absolute; top:-20%; left:50%; transform:translateX(-50%);
          width:700px; height:700px;
          background:radial-gradient(ellipse,rgba(201,168,76,0.06) 0%,transparent 65%);
          pointer-events:none;
        }

        .auth-corner { position:absolute; width:80px; height:80px; pointer-events:none; }
        .auth-corner.tl { top:80px;left:24px; border-top:1px solid rgba(201,168,76,0.25); border-left:1px solid rgba(201,168,76,0.25); }
        .auth-corner.br { bottom:24px;right:24px; border-bottom:1px solid rgba(201,168,76,0.25); border-right:1px solid rgba(201,168,76,0.25); }

        .auth-card {
          position:relative; z-index:2; width:100%; max-width:440px;
          background:rgba(255,255,255,0.025); border:1px solid rgba(201,168,76,0.2);
          border-radius:4px; padding:3rem 2.5rem 2.5rem; animation:authFadeUp 0.7s ease both;
        }
        @keyframes authFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .auth-card::before {
          content:''; position:absolute; top:0; left:10%; right:10%; height:1px;
          background:linear-gradient(90deg,transparent,var(--gold),transparent);
        }

        .auth-eyebrow { font-size:0.62rem; letter-spacing:5px; text-transform:uppercase; color:var(--gold); text-align:center; margin-bottom:0.6rem; }
        .auth-title { font-family:'Cormorant Garamond',serif; font-size:2.3rem; font-weight:700; color:#fff; text-align:center; line-height:1.1; margin-bottom:0.3rem; }
        .auth-title em { font-style:italic; color:var(--gold-light); }
        .auth-divider { width:40px; height:1px; background:var(--gold); margin:1.2rem auto 1.5rem; opacity:0.7; }
        .auth-subtitle { font-size:0.83rem; color:rgba(255,255,255,0.4); text-align:center; margin-bottom:2rem; font-weight:300; line-height:1.75; }

        /* Sent state */
        .sent-box {
          text-align:center; padding:1rem 0 0.5rem;
          animation:authFadeUp 0.5s ease both;
        }
        .sent-icon { font-size:2.5rem; color:var(--gold); display:block; margin-bottom:1rem; }
        .sent-title { font-family:'Cormorant Garamond',serif; font-size:1.6rem; color:#fff; margin-bottom:0.6rem; }
        .sent-text { font-size:0.85rem; color:rgba(255,255,255,0.5); line-height:1.75; font-weight:300; }
        .sent-note { font-size:0.75rem; color:rgba(255,255,255,0.25); margin-top:1rem; line-height:1.6; }

        .auth-error { background:rgba(220,53,69,0.1); border:1px solid rgba(220,53,69,0.3); border-radius:3px; padding:0.75rem 1rem; font-size:0.82rem; color:#f8908a; margin-bottom:1.5rem; display:flex; align-items:center; gap:0.5rem; }

        .auth-field { margin-bottom:1.4rem; }
        .auth-label { display:block; font-size:0.65rem; letter-spacing:3px; text-transform:uppercase; color:rgba(255,255,255,0.4); margin-bottom:0.55rem; font-weight:500; }
        .auth-input { width:100%; background:rgba(255,255,255,0.04); border:1px solid rgba(201,168,76,0.18); border-radius:3px; padding:0.85rem 1rem; color:#fff; font-family:'Jost',sans-serif; font-size:0.9rem; font-weight:300; outline:none; transition:border-color 0.25s,background 0.25s; -webkit-appearance:none; }
        .auth-input::placeholder { color:rgba(255,255,255,0.2); }
        .auth-input:focus { border-color:rgba(201,168,76,0.55); background:rgba(201,168,76,0.04); }

        .auth-submit {
          width:100%; background:var(--gold); border:none; border-radius:3px; padding:0.95rem;
          color:#0A0A0A; font-family:'Jost',sans-serif; font-weight:600; font-size:0.82rem;
          letter-spacing:3px; text-transform:uppercase; cursor:pointer;
          transition:background 0.3s,transform 0.2s,opacity 0.2s; margin-top:0.5rem;
          position:relative; overflow:hidden;
        }
        .auth-submit::after { content:''; position:absolute; inset:0; background:linear-gradient(90deg,transparent,rgba(255,255,255,0.14),transparent); transform:translateX(-100%); transition:transform 0.55s; }
        .auth-submit:hover::after { transform:translateX(100%); }
        .auth-submit:hover { background:var(--gold-light); transform:translateY(-1px); }
        .auth-submit:disabled { opacity:0.55; cursor:not-allowed; transform:none; }

        .auth-back { display:flex; align-items:center; justify-content:center; gap:0.4rem; margin-top:1.8rem; font-size:0.8rem; color:rgba(255,255,255,0.3); text-decoration:none; transition:color 0.2s; }
        .auth-back:hover { color:var(--gold); }

        .auth-spinner { display:inline-block; width:14px; height:14px; border:2px solid rgba(10,10,10,0.3); border-top-color:#0A0A0A; border-radius:50%; animation:spin 0.7s linear infinite; vertical-align:middle; margin-right:0.5rem; }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>

      <div className="auth-root">
        <div className="auth-corner tl" />
        <div className="auth-corner br" />

        <div className="auth-card">
          <p className="auth-eyebrow">Nairobi's Ultimate Experience</p>
          <h1 className="auth-title">Reset <em>Password</em></h1>
          <div className="auth-divider" />

          {sent ? (
            <div className="sent-box">
              <span className="sent-icon">✉</span>
              <h2 className="sent-title">Check Your Inbox</h2>
              <p className="sent-text">
                If an account exists for <strong style={{ color: "#fff" }}>{email}</strong>,
                you'll receive a password reset link shortly.
              </p>
              <p className="sent-note">
                Didn't receive it? Check your spam folder or{" "}
                <span
                  style={{ color: "var(--gold)", cursor: "pointer" }}
                  onClick={() => { setSent(false); }}
                >
                  try again
                </span>.
              </p>
            </div>
          ) : (
            <>
              <p className="auth-subtitle">
                Enter your email address and we'll send you a secure link to reset your password.
              </p>
              {error && <div className="auth-error"><span>✕</span> {error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="fp-email">Email Address</label>
                  <input
                    id="fp-email"
                    type="email"
                    className="auth-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                <button type="submit" className="auth-submit" disabled={loading}>
                  {loading && <span className="auth-spinner" />}
                  {loading ? "Sending…" : "Send Reset Link"}
                </button>
              </form>
            </>
          )}

          <Link to="/login" className="auth-back">← Back to Sign In</Link>
        </div>
      </div>
    </>
  );
}

export default ForgotPassword;
